import { useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import type {
  SsoConnection,
  SsoConnectionInsert,
  SsoConnectionUpdate,
  SsoLoginAttempt,
  Application
} from '../types/database'

interface SsoConnectionWithApp extends SsoConnection {
  applications?: Application
}

interface UseSsoConnectionsReturn {
  connections: SsoConnectionWithApp[]
  loading: boolean
  error: string | null
  loadConnections: (organizationId: string) => Promise<void>
  getConnection: (id: string) => Promise<SsoConnectionWithApp | null>
  createConnection: (data: SsoConnectionInsert) => Promise<SsoConnection | null>
  updateConnection: (id: string, data: SsoConnectionUpdate) => Promise<boolean>
  deleteConnection: (id: string) => Promise<boolean>
  toggleActive: (id: string, isActive: boolean) => Promise<boolean>
  getLoginAttempts: (connectionId: string, limit?: number) => Promise<SsoLoginAttempt[]>
  testConnection: (id: string) => Promise<{ success: boolean; message: string }>
}

export function useSsoConnections(): UseSsoConnectionsReturn {
  const [connections, setConnections] = useState<SsoConnectionWithApp[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadConnections = useCallback(async (organizationId: string) => {
    try {
      setLoading(true)
      setError(null)

      const { data, error: fetchError } = await supabase
        .from('sso_connections')
        .select(`
          *,
          applications(id, name, image_url, app_key)
        `)
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })

      if (fetchError) throw fetchError

      setConnections(data || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load SSO connections')
    } finally {
      setLoading(false)
    }
  }, [])

  const getConnection = async (id: string): Promise<SsoConnectionWithApp | null> => {
    try {
      const { data, error: fetchError } = await supabase
        .from('sso_connections')
        .select(`
          *,
          applications(id, name, image_url, app_key)
        `)
        .eq('id', id)
        .single()

      if (fetchError) throw fetchError

      return data
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get SSO connection')
      return null
    }
  }

  const createConnection = async (data: SsoConnectionInsert): Promise<SsoConnection | null> => {
    try {
      // Generate SP Entity ID for SAML if not provided
      const insertData = { ...data }
      if (data.protocol === 'saml' && !data.saml_idp_entity_id) {
        // Will be set by default in DB
      }

      const { data: created, error: createError } = await supabase
        .from('sso_connections')
        .insert(insertData)
        .select(`
          *,
          applications(id, name, image_url, app_key)
        `)
        .single()

      if (createError) throw createError

      setConnections(prev => [created, ...prev])
      return created
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create SSO connection')
      return null
    }
  }

  const updateConnection = async (id: string, data: SsoConnectionUpdate): Promise<boolean> => {
    try {
      const { error: updateError } = await supabase
        .from('sso_connections')
        .update(data)
        .eq('id', id)

      if (updateError) throw updateError

      setConnections(prev =>
        prev.map(conn => conn.id === id ? { ...conn, ...data } : conn)
      )
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update SSO connection')
      return false
    }
  }

  const deleteConnection = async (id: string): Promise<boolean> => {
    try {
      const { error: deleteError } = await supabase
        .from('sso_connections')
        .delete()
        .eq('id', id)

      if (deleteError) throw deleteError

      setConnections(prev => prev.filter(conn => conn.id !== id))
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete SSO connection')
      return false
    }
  }

  const toggleActive = async (id: string, isActive: boolean): Promise<boolean> => {
    return updateConnection(id, { is_active: isActive })
  }

  const getLoginAttempts = async (connectionId: string, limit = 50): Promise<SsoLoginAttempt[]> => {
    try {
      const { data, error: fetchError } = await supabase
        .from('sso_login_attempts')
        .select('*')
        .eq('sso_connection_id', connectionId)
        .order('created_at', { ascending: false })
        .limit(limit)

      if (fetchError) throw fetchError

      return data || []
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get login attempts')
      return []
    }
  }

  const testConnection = async (id: string): Promise<{ success: boolean; message: string }> => {
    try {
      const connection = await getConnection(id)
      if (!connection) {
        return { success: false, message: 'Connection not found' }
      }

      if (connection.protocol === 'saml') {
        // Validate SAML configuration
        if (!connection.saml_idp_entity_id || !connection.saml_idp_sso_url || !connection.saml_idp_certificate) {
          return { success: false, message: 'Missing required SAML configuration (Entity ID, SSO URL, or Certificate)' }
        }

        // Try to parse the certificate
        try {
          const certContent = connection.saml_idp_certificate
          if (!certContent.includes('BEGIN CERTIFICATE')) {
            return { success: false, message: 'Invalid certificate format. Must be in PEM format.' }
          }
        } catch {
          return { success: false, message: 'Invalid certificate format' }
        }

        return { success: true, message: 'SAML configuration is valid' }
      } else if (connection.protocol === 'oidc') {
        // Validate OIDC configuration
        if (!connection.oidc_client_id || !connection.oidc_client_secret) {
          return { success: false, message: 'Missing required OIDC configuration (Client ID or Client Secret)' }
        }

        if (!connection.oidc_authorization_url || !connection.oidc_token_url) {
          return { success: false, message: 'Missing required OIDC URLs (Authorization URL or Token URL)' }
        }

        // Try to fetch the discovery document if issuer is provided
        if (connection.oidc_issuer) {
          try {
            const discoveryUrl = connection.oidc_issuer.replace(/\/$/, '') + '/.well-known/openid-configuration'
            const response = await fetch(discoveryUrl)
            if (!response.ok) {
              return { success: false, message: `Failed to fetch OIDC discovery document from ${discoveryUrl}` }
            }
          } catch {
            return { success: false, message: 'Failed to connect to OIDC issuer' }
          }
        }

        return { success: true, message: 'OIDC configuration is valid' }
      }

      return { success: false, message: 'Unknown protocol' }
    } catch (err) {
      return { success: false, message: err instanceof Error ? err.message : 'Test failed' }
    }
  }

  return {
    connections,
    loading,
    error,
    loadConnections,
    getConnection,
    createConnection,
    updateConnection,
    deleteConnection,
    toggleActive,
    getLoginAttempts,
    testConnection
  }
}
